/**
 * Page Capability Hooks
 *
 * Provides React hooks for checking page-level capabilities and role-specific access.
 * Includes caching and batch loading for performance optimization.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '@/services/apiClient';
import {
  PageCapabilityState,
  PageCapabilityWithOrgStatus,
  PageAccessCheckResult,
  BulkPageAccessResult,
  DisplayBehavior,
  PageActionPermissions,
  PageCategory,
  groupPagesByCategory,
  PagesByCategory,
} from '@/types/pageCapabilities';
import { CBORoleType } from '@/types/cboRoles';
import { clearSidebarAccessCache } from './useSidebarPageAccess';

// ============================================================================
// CACHE CONFIGURATION
// ============================================================================

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  error?: boolean;
}

const PAGE_CACHE = new Map<string, CacheEntry<PageAccessCheckResult>>();
const BULK_CACHE = new Map<string, CacheEntry<BulkPageAccessResult>>();
const LIST_CACHE = new Map<string, CacheEntry<PageCapabilityWithOrgStatus[]>>();

const CACHE_TTL_SUCCESS = 60000; // 1 minute for successful results
const CACHE_TTL_ERROR = 10000;   // 10 seconds for error results

// Track in-flight requests to prevent duplicate calls
const inFlightRequests = new Map<string, Promise<any>>();

function getCacheKey(orgId: number, pageKey: string, roleType?: string): string {
  return `${orgId}:${pageKey}:${roleType || 'default'}`;
}

function getBulkCacheKey(orgId: number, pageKeys: string[], roleType?: string): string {
  return `${orgId}:bulk:${pageKeys.sort().join(',')}:${roleType || 'default'}`;
}

function isCacheValid<T>(entry: CacheEntry<T> | undefined): boolean {
  if (!entry) return false;
  const ttl = entry.error ? CACHE_TTL_ERROR : CACHE_TTL_SUCCESS;
  return Date.now() - entry.timestamp < ttl;
}

/**
 * Clear all page capability caches
 */
export function clearPageCapabilityCache(): void {
  PAGE_CACHE.clear();
  BULK_CACHE.clear();
  LIST_CACHE.clear();
}

/**
 * Clear cache for a specific organization
 */
export function clearOrgPageCapabilityCache(orgId: number): void {
  const prefix = `${orgId}:`;
  for (const key of PAGE_CACHE.keys()) {
    if (key.startsWith(prefix)) PAGE_CACHE.delete(key);
  }
  for (const key of BULK_CACHE.keys()) {
    if (key.startsWith(prefix)) BULK_CACHE.delete(key);
  }
  for (const key of LIST_CACHE.keys()) {
    if (key.startsWith(prefix)) LIST_CACHE.delete(key);
  }
  // Also clear the sidebar access cache so navigation updates immediately
  clearSidebarAccessCache(orgId);

  // Dispatch event to notify sidebar to refetch
  window.dispatchEvent(new CustomEvent('pagePermissionsUpdated', { detail: { orgId } }));
}

// ============================================================================
// SINGLE PAGE CAPABILITY HOOK
// ============================================================================

/**
 * Hook to check if a user has access to a specific page
 *
 * @param organizationId - The organization ID
 * @param pageKey - The page key to check (e.g., 'members.list', 'billing.analytics')
 * @param roleType - Optional role type override (defaults to current user's role)
 * @returns PageCapabilityState with access information
 *
 * @example
 * ```tsx
 * const { hasAccess, loading, permissions } = usePageCapability(orgId, 'billing.analytics');
 *
 * if (loading) return <Spinner />;
 * if (!hasAccess) return <AccessDenied />;
 *
 * return <BillingAnalytics canExport={permissions.export} />;
 * ```
 */
export function usePageCapability(
  organizationId: number | null,
  pageKey: string,
  roleType?: CBORoleType
): PageCapabilityState {
  const [state, setState] = useState<PageCapabilityState>({
    enabled: false,
    hasAccess: false,
    loading: true,
    config: {},
    displayBehavior: 'hide',
    permissions: {},
    accessSource: 'default',
  });

  useEffect(() => {
    if (!organizationId || !pageKey) {
      setState(prev => ({ ...prev, loading: false, hasAccess: false }));
      return;
    }

    const cacheKey = getCacheKey(organizationId, pageKey, roleType);

    // Check cache first
    const cached = PAGE_CACHE.get(cacheKey);
    if (isCacheValid(cached)) {
      setState({
        enabled: cached!.data.enabled,
        hasAccess: cached!.data.has_access,
        loading: false,
        config: {},
        displayBehavior: cached!.data.display_behavior,
        permissions: cached!.data.permissions,
        accessSource: cached!.data.access_source,
      });
      return;
    }

    // Check if request is already in flight
    const existingRequest = inFlightRequests.get(cacheKey);
    if (existingRequest) {
      existingRequest.then((result: PageAccessCheckResult) => {
        setState({
          enabled: result.enabled,
          hasAccess: result.has_access,
          loading: false,
          config: {},
          displayBehavior: result.display_behavior,
          permissions: result.permissions,
          accessSource: result.access_source,
        });
      });
      return;
    }

    // Make API call
    const fetchAccess = async () => {
      try {
        setState(prev => ({ ...prev, loading: true }));

        const url = roleType
          ? `/api/organizations/${organizationId}/page-access/${pageKey}?role_type=${roleType}`
          : `/api/organizations/${organizationId}/page-access/${pageKey}`;

        const response = await apiClient.get(url, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        });

        const result: PageAccessCheckResult = response.data;

        // Cache the result
        PAGE_CACHE.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
        });

        setState({
          enabled: result.enabled,
          hasAccess: result.has_access,
          loading: false,
          config: {},
          displayBehavior: result.display_behavior,
          permissions: result.permissions,
          accessSource: result.access_source,
        });

        return result;
      } catch (error) {
        console.error(`Error checking page capability for ${pageKey}:`, error);

        // Cache the error result
        const errorResult: PageAccessCheckResult = {
          page_key: pageKey,
          has_access: false,
          access_source: 'page_not_found',
          enabled: false,
          display_behavior: 'hide',
          permissions: {},
        };

        PAGE_CACHE.set(cacheKey, {
          data: errorResult,
          timestamp: Date.now(),
          error: true,
        });

        setState({
          enabled: false,
          hasAccess: false,
          loading: false,
          config: {},
          displayBehavior: 'hide',
          permissions: {},
          accessSource: 'page_not_found',
        });

        return errorResult;
      } finally {
        inFlightRequests.delete(cacheKey);
      }
    };

    const requestPromise = fetchAccess();
    inFlightRequests.set(cacheKey, requestPromise);
  }, [organizationId, pageKey, roleType]);

  return state;
}

// ============================================================================
// BULK PAGE CAPABILITIES HOOK
// ============================================================================

/**
 * Hook to check access for multiple pages at once
 * More efficient than individual calls for sidebar rendering
 *
 * @param organizationId - The organization ID
 * @param pageKeys - Array of page keys to check
 * @param roleType - Optional role type override
 * @returns Object with page access results and loading state
 *
 * @example
 * ```tsx
 * const { results, loading } = usePageCapabilities(orgId, [
 *   'members.list', 'billing.analytics', 'communications.inbox'
 * ]);
 *
 * const canAccessBilling = results['billing.analytics']?.has_access;
 * ```
 */
export function usePageCapabilities(
  organizationId: number | null,
  pageKeys: string[],
  roleType?: CBORoleType
): { results: BulkPageAccessResult; loading: boolean } {
  const [results, setResults] = useState<BulkPageAccessResult>({});
  const [loading, setLoading] = useState(true);

  // Memoize page keys to prevent unnecessary re-renders
  const sortedKeys = useMemo(() => [...pageKeys].sort(), [pageKeys]);
  const keysRef = useRef(sortedKeys.join(','));

  useEffect(() => {
    keysRef.current = sortedKeys.join(',');
  }, [sortedKeys]);

  useEffect(() => {
    if (!organizationId || sortedKeys.length === 0) {
      setLoading(false);
      return;
    }

    const cacheKey = getBulkCacheKey(organizationId, sortedKeys, roleType);

    // Check cache first
    const cached = BULK_CACHE.get(cacheKey);
    if (isCacheValid(cached)) {
      setResults(cached!.data);
      setLoading(false);
      return;
    }

    const fetchBulkAccess = async () => {
      try {
        setLoading(true);

        const url = roleType
          ? `/api/organizations/${organizationId}/page-access/bulk-check?role_type=${roleType}`
          : `/api/organizations/${organizationId}/page-access/bulk-check`;

        const response = await apiClient.post(url, sortedKeys, {
          headers: { 'X-CBO-ID': organizationId.toString() }
        });

        const data: BulkPageAccessResult = response.data;

        // Cache the result
        BULK_CACHE.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });

        // Also cache individual results
        for (const [pageKey, access] of Object.entries(data)) {
          const individualKey = getCacheKey(organizationId, pageKey, roleType);
          PAGE_CACHE.set(individualKey, {
            data: {
              page_key: pageKey,
              has_access: access.has_access,
              access_source: access.access_source as PageAccessCheckResult['access_source'],
              enabled: access.enabled,
              display_behavior: access.display_behavior,
              permissions: {},
            },
            timestamp: Date.now(),
          });
        }

        setResults(data);
      } catch (error) {
        console.error('Error checking bulk page capabilities:', error);
        setResults({});
      } finally {
        setLoading(false);
      }
    };

    fetchBulkAccess();
  }, [organizationId, keysRef.current, roleType]);

  return { results, loading };
}

// ============================================================================
// PAGE CAPABILITIES LIST HOOK
// ============================================================================

/**
 * Hook to load all page capabilities for an organization
 * Used in admin UI for managing page access
 *
 * @param organizationId - The organization ID
 * @param category - Optional category filter
 * @returns Object with pages list, grouped pages, and loading state
 */
export function usePageCapabilitiesList(
  organizationId: number | null,
  category?: PageCategory
): {
  pages: PageCapabilityWithOrgStatus[];
  pagesByCategory: PagesByCategory;
  loading: boolean;
  error: string | null;
  refetch: (silent?: boolean) => void;
  patchPage: (pageKey: string, partial: Partial<PageCapabilityWithOrgStatus>) => void;
} {
  const [pages, setPages] = useState<PageCapabilityWithOrgStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Optimistically patch a single page in local state. Used by the admin UI
  // on toggle so the switch flips instantly without a full refetch that would
  // unmount the (scrolled) list and reset scroll to top.
  const patchPage = useCallback((pageKey: string, partial: Partial<PageCapabilityWithOrgStatus>) => {
    setPages(prev => prev.map(p => (p.page_key === pageKey ? { ...p, ...partial } : p)));
  }, []);

  // `silent` skips the loading spinner so a post-save reconcile re-renders the
  // list in place instead of unmounting it (the scroll-to-top bug).
  const fetchPages = useCallback(async (silent = false) => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const cacheKey = `${organizationId}:list:${category || 'all'}`;

    // Check cache
    const cached = LIST_CACHE.get(cacheKey);
    if (isCacheValid(cached)) {
      setPages(cached!.data);
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);

      const url = category
        ? `/api/organizations/${organizationId}/page-capabilities?category=${category}`
        : `/api/organizations/${organizationId}/page-capabilities`;

      const response = await apiClient.get(url, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      const data: PageCapabilityWithOrgStatus[] = response.data;

      // Cache the result
      LIST_CACHE.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      setPages(data);
    } catch (err: any) {
      console.error('Error loading page capabilities:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load page capabilities');
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, category]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const pagesByCategory = useMemo(() => groupPagesByCategory(pages), [pages]);

  return {
    pages,
    pagesByCategory,
    loading,
    error,
    refetch: fetchPages,
    patchPage,
  };
}

// ============================================================================
// UPDATE HOOKS
// ============================================================================

/**
 * Hook for updating page capabilities
 * Returns functions to toggle and configure page access
 */
export function usePageCapabilityUpdate(organizationId: number | null) {
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updatePageCapability = useCallback(async (
    pageKey: string,
    enabled: boolean,
    config?: Record<string, unknown>,
    displayBehavior?: DisplayBehavior
  ): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      setSaving(pageKey);
      setError(null);

      await apiClient.put(
        `/api/organizations/${organizationId}/page-capabilities/${pageKey}`,
        { enabled, config: config || {}, display_behavior: displayBehavior || 'hide' },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      // Clear cache for this org
      clearOrgPageCapabilityCache(organizationId);

      return true;
    } catch (err: any) {
      console.error('Error updating page capability:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update');
      return false;
    } finally {
      setSaving(null);
    }
  }, [organizationId]);

  const togglePageCapability = useCallback(async (
    pageKey: string,
    currentEnabled: boolean
  ): Promise<boolean> => {
    return updatePageCapability(pageKey, !currentEnabled);
  }, [updatePageCapability]);

  const resetPageCapability = useCallback(async (pageKey: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      setSaving(pageKey);
      setError(null);

      await apiClient.delete(
        `/api/organizations/${organizationId}/page-capabilities/${pageKey}`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      clearOrgPageCapabilityCache(organizationId);
      return true;
    } catch (err: any) {
      console.error('Error resetting page capability:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to reset');
      return false;
    } finally {
      setSaving(null);
    }
  }, [organizationId]);

  return {
    updatePageCapability,
    togglePageCapability,
    resetPageCapability,
    saving,
    error,
  };
}

// ============================================================================
// ROLE PAGE PERMISSIONS HOOKS
// ============================================================================

/**
 * Hook to load role page permissions for an organization
 * @param roleType - Can be a system role (CBORoleType) or an organization role type string
 */
export function useRolePagePermissions(
  organizationId: number | null,
  roleType: string | null,
  category?: PageCategory
) {
  const [permissions, setPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialLoad = useRef(true);

  const fetchPermissions = useCallback(async (isRefetch = false) => {
    if (!organizationId || !roleType) {
      setLoading(false);
      return;
    }

    try {
      // Only show full loading spinner on initial load, not refetch
      if (isRefetch) {
        setIsRefetching(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const url = category
        ? `/api/organizations/${organizationId}/role-permissions/${roleType}?category=${category}`
        : `/api/organizations/${organizationId}/role-permissions/${roleType}`;

      const response = await apiClient.get(url, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      setPermissions(response.data);
    } catch (err: any) {
      console.error('Error loading role page permissions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
      setIsRefetching(false);
    }
  }, [organizationId, roleType, category]);

  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      fetchPermissions(false);
    } else {
      // Role or category changed, do a full reload
      fetchPermissions(false);
    }
  }, [fetchPermissions]);

  // Refetch function that doesn't trigger loading spinner
  const refetch = useCallback(() => {
    fetchPermissions(true);
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook for updating role page permissions
 * Accepts both system roles (CBORoleType) and organization role types
 */
export function useRolePagePermissionUpdate(organizationId: number | null) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRolePagePermission = useCallback(async (
    roleType: string,
    pageKey: string,
    accessLevel: 'enabled' | 'disabled' | 'inherit',
    permissions?: PageActionPermissions,
    notes?: string
  ): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      setSaving(true);
      setError(null);

      await apiClient.put(
        `/api/organizations/${organizationId}/role-permissions/${roleType}/${pageKey}`,
        { page_key: pageKey, access_level: accessLevel, permissions: permissions || {}, notes },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      clearOrgPageCapabilityCache(organizationId);
      return true;
    } catch (err: any) {
      console.error('Error updating role page permission:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update');
      return false;
    } finally {
      setSaving(false);
    }
  }, [organizationId]);

  const bulkUpdateRolePermissions = useCallback(async (
    roleType: string,
    updates: Array<{ page_key: string; access_level: 'enabled' | 'disabled' | 'inherit' }>
  ): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      setSaving(true);
      setError(null);

      await apiClient.post(
        `/api/organizations/${organizationId}/role-permissions/${roleType}/bulk-update`,
        updates,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      clearOrgPageCapabilityCache(organizationId);
      return true;
    } catch (err: any) {
      console.error('Error bulk updating role permissions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update');
      return false;
    } finally {
      setSaving(false);
    }
  }, [organizationId]);

  const resetAllRolePermissions = useCallback(async (roleType: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      setSaving(true);
      setError(null);

      await apiClient.delete(
        `/api/organizations/${organizationId}/role-permissions/${roleType}`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      clearOrgPageCapabilityCache(organizationId);
      return true;
    } catch (err: any) {
      console.error('Error resetting role permissions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to reset');
      return false;
    } finally {
      setSaving(false);
    }
  }, [organizationId]);

  /**
   * Update permissions for all pages in a category at once
   */
  const updateCategoryPermissions = useCallback(async (
    roleType: string,
    category: string,
    accessLevel: 'enabled' | 'disabled' | 'inherit',
    notes?: string
  ): Promise<{ success: boolean; pagesAffected: number }> => {
    if (!organizationId) return { success: false, pagesAffected: 0 };

    try {
      setSaving(true);
      setError(null);

      const response = await apiClient.post(
        `/api/organizations/${organizationId}/role-permissions/${roleType}/category/${category}`,
        { access_level: accessLevel, notes },
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      clearOrgPageCapabilityCache(organizationId);
      return {
        success: true,
        pagesAffected: response.data.pages_affected || 0
      };
    } catch (err: any) {
      console.error('Error updating category permissions:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to update category');
      return { success: false, pagesAffected: 0 };
    } finally {
      setSaving(false);
    }
  }, [organizationId]);

  return {
    updateRolePagePermission,
    bulkUpdateRolePermissions,
    resetAllRolePermissions,
    updateCategoryPermissions,
    saving,
    error,
  };
}

// ============================================================================
// PERMISSION MATRIX HOOK
// ============================================================================

/**
 * Hook to load the role permissions matrix view
 * Shows all roles side by side for comparison
 */
export function usePermissionMatrix(
  organizationId: number | null,
  category?: PageCategory
) {
  const [matrix, setMatrix] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `silent` skips the spinner so a post-save reconcile re-renders the matrix
  // in place rather than unmounting it (which reset scroll to the top).
  const fetchMatrix = useCallback(async (silent = false) => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);

      const url = category
        ? `/api/organizations/${organizationId}/role-permissions/matrix?category=${category}`
        : `/api/organizations/${organizationId}/role-permissions/matrix`;

      const response = await apiClient.get(url, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      // Handle both response.data (normal) and response.data.data (double-wrapped)
      const payload = response.data;
      const matrixData = payload?.roles ? payload : payload?.data?.roles ? payload.data : payload;
      console.log('[usePermissionMatrix] raw response keys:', Object.keys(payload || {}), 'roles?', !!matrixData?.roles, 'matrix?', !!matrixData?.matrix);
      setMatrix(matrixData);
    } catch (err: any) {
      console.error('Error loading permission matrix:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load matrix');
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, category]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  return {
    matrix,
    loading,
    error,
    refetch: fetchMatrix,
  };
}

// ============================================================================
// ORGANIZATION ROLES HOOK
// ============================================================================

/**
 * Organization role for role page permission management
 */
export interface OrganizationRoleInfo {
  id?: number;
  type: string;
  display_name: string;
  display_name_es?: string;
  description?: string;
  system_role?: string;
  abbreviation?: string;
  icon?: string;
  color?: string;
  is_field_worker?: boolean;
  sort_order?: number;
  access_level?: number;
  can_be_restricted: boolean;
  is_organization_role: boolean;
  override_count?: number;
  enabled_overrides?: number;
  disabled_overrides?: number;
}

/**
 * Hook to fetch organization-specific roles for role page permission management
 *
 * Returns both organization roles (from role_terminology) and system roles
 * that can have page permissions configured.
 *
 * @param organizationId - The organization ID
 * @param includeSystemRoles - Whether to include system roles that don't have org-specific definitions
 * @returns List of roles available for permission configuration
 */
export function useOrganizationRoles(
  organizationId: number | null,
  includeSystemRoles: boolean = true
) {
  const [roles, setRoles] = useState<OrganizationRoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!organizationId) {
      setRoles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const url = `/api/organizations/${organizationId}/available-roles?include_system_roles=${includeSystemRoles}`;

      const response = await apiClient.get(url, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      setRoles(response.data);
    } catch (err: any) {
      console.error('Error loading organization roles:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, includeSystemRoles]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
}

/**
 * Hook to fetch organization roles with their permission summary
 * Shows how many page overrides each role has
 */
export function useOrganizationRolesWithSummary(organizationId: number | null) {
  const [roles, setRoles] = useState<OrganizationRoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!organizationId) {
      setRoles([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(
        `/api/organizations/${organizationId}/roles-permission-summary`,
        { headers: { 'X-CBO-ID': organizationId.toString() } }
      );

      setRoles(response.data);
    } catch (err: any) {
      console.error('Error loading organization roles with summary:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load roles');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    roles,
    loading,
    error,
    refetch: fetchRoles,
  };
}
